<template>
  <Modal
    :loading="busy"
    :size="['big', 'max-height']"
    pageFeed
    :footer="footer"
    :title="`連携${isMail ? 'メール内容' : '履歴'}`"
    @close="close"
  >
    <div class="mail">
      <ul class="list">
        <template v-for="(item, i) in syncList">
          <li class="item" v-if="!isShowMessage || current === i" :key="i" @click="openMessage(i)">
            <div class="info">
              <ReceptionStatus class="status" :status="item.status" />
              <span v-if="item.alertType" class="note">
                ※ 変更・キャンセルの取込対象外です
              </span>
              <span class="time">{{ formatDate(item.lastUpdateTime, 'YYYY/M/D (dd) HH：mm') }}</span>
            </div>
            <h3 class="subject">{{ item.subject }}</h3>
          </li>
        </template>
      </ul>
      <p class="message" v-if="html" v-html="html" />
    </div>
    <template #footer-left v-if="isShowMessage">
      <Button @click="closeMessage">一覧に戻る</Button>
    </template>
    <template #footer-center v-if="!isShowMessage">
      <Button @click="close">閉じる</Button>
    </template>
    <template #footer-right v-if="isShowMessage">
      <button class="button arw--btn prev--btn" :disabled="isPrevDisabled" @click="prev"></button>
      <button class="button arw--btn next--btn" :disabled="current === 0" @click="next"></button>
    </template>
  </Modal>
</template>

<script>
import { escape } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import urlRegex from 'url-regex'
import { formatDate } from '@/utils/date'
import { isWebView } from '@/utils/device'
import Modal from '@/components/molecules/Modal'
import Button from '@/components/atoms/Button'
import ReceptionStatus from '@/components/atoms/ReceptionStatus'

export default {
  name: 'SyncMessageModal',
  components: {
    Modal,
    Button,
    ReceptionStatus,
  },
  props: {
    mode: {
      String,
      required: true,
      validator: value => ['mail', 'history'].includes(value),
    },
  },
  data() {
    return {
      current: '',
      footer: true,
      syncList: [],
    }
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
      reserve: state => state.reserves.detail.reservation,
      syncMails: state => state.reserves.syncMails,
      syncHistory: state => state.reserves.resraku,
    }),
    ...mapGetters({
      allMails: 'reserves/syncMails/all',
      allHistorys: 'reserves/resraku/all',
    }),
    mail() {
      return this.allMails[this.current] || {}
    },
    history() {
      return this.allHistorys[this.current] || {}
    },
    html() {
      return escape(this.mail.body || this.history.body || '').replace(urlRegex(), href => {
        const url = new URL(href)
        if (isWebView()) url.searchParams.append('browser', 'safari')
        return `<a href="${url.href}" target="_blank">${href}</a>`
      })
    },
    isShowMessage() {
      return Number.isInteger(this.current) && (this.allMails.length > 1 || this.allHistorys.length > 1)
    },
    isPrevDisabled() {
      return this.current === (this.isMail ? this.allMails : this.allHistorys).length - 1
    },
    isMail() {
      return this.mode === 'mail'
    },
    busy() {
      return this.syncMails.busy || this.syncHistory.busy
    },
  },
  watch: {
    allMails(v) {
      if (v.length === 1) this.current = 0
    },
    allHistorys(v) {
      if (v.length === 1) this.current = 0
    },
  },
  async mounted() {
    this.footer = !this.isSP
    if (this.isMail) {
      await this.$store.dispatch('reserves/syncMails/get', this.reserve.id)
      this.syncList = this.syncMails.list
    } else {
      await this.$store.dispatch('reserves/resraku/get', this.reserve.id)
      this.syncList = this.syncHistory.list
    }
  },
  beforeDestroy() {
    if (this.isMail) {
      this.$store.commit('reserves/syncMails/clear')
    } else {
      this.$store.commit('reserves/resraku/clear')
    }
    this.current = ''
  },
  methods: {
    formatDate,
    close() {
      this.$emit('close')
    },
    openMessage(i) {
      if (this.isSP) this.footer = true
      this.current = i
    },
    closeMessage() {
      if (this.isSP) this.footer = false
      this.current = ''
    },
    prev() {
      this.current++
    },
    next() {
      this.current--
    },
  },
}
</script>

<style lang="postcss" scoped>
.mail {
  .responsive & {
    @media screen and (--sp-modal) {
      margin-bottom: 16px;
    }
  }
}

.item {
  border-bottom: 1px solid var(--very-light-gray-2);
  cursor: pointer;
  padding: 16px 8px;
}

.info {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.status {
  height: 24px;
  .responsive & {
    @media screen and (--sp-size) {
      font-size: 12px;
      width: 70px;
    }
  }
}

.note {
  color: var(--pure-red);
  font-size: 11px;
  margin-left: 8px;
  margin-right: auto;
}

.time {
  font-size: 13px;
  line-height: 1;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 11px;
    }
  }
}

.subject {
  font-size: 15px;
  font-weight: bold;
  line-height: 1.7;
  margin-top: 12px;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 20px;
      line-height: 1.4;
    }
  }
}

.message {
  font-size: 13px;
  line-height: 1.78;
  padding: 28px 8px 0;
  white-space: pre-wrap;
}

.arw--btn {
  appearance: none;
  background: var(--white);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 1px var(--very-light-gray);
  box-sizing: border-box;
  color: var(--brand-color);
  cursor: pointer;
  display: block;
  font-size: 21px;
  font-weight: bold;
  height: 56px;
  line-height: 54px;
  margin: 0;
  outline: 0;
  padding: 0;
  position: relative;
  text-align: center;
  width: 160px;

  &::before {
    border-right: 4px solid var(--moderate-blue);
    border-top: 4px solid var(--moderate-blue);
    bottom: 0;
    content: '';
    height: 13px;
    margin: auto;
    position: absolute;
    top: 0;
    width: 13px;
  }

  &:disabled {
    background: var(--white);
    border: 1px solid var(--border-color);
  }

  &:disabled::before {
    border-right: 4px solid var(--very-light-gray);
    border-top: 4px solid var(--very-light-gray);
  }

  .responsive &:disabled {
    @media screen and (--sp-modal) {
      background: var(--light-gray);
    }
  }

  .responsive .footer & {
    @media screen and (--sp-modal) {
      width: 76px;

      &::before {
        border-width: 2px;
        height: 6px;
        width: 6px;
      }
      &:disabled::before {
        background: var(--light-gray);
        border-width: 2px;
      }
    }
  }
}

.prev--btn {
  width: 144px;

  &::before {
    left: 65px;
    transform: rotate(-135deg);
  }

  .responsive & {
    @media screen and (--sp-modal) {
      &::before {
        left: 35px;
      }
    }
  }
}

.next--btn {
  width: 144px;

  &::before {
    left: 60px;
    transform: rotate(45deg);
  }

  .responsive & {
    @media screen and (--sp-modal) {
      &::before {
        left: 30px;
      }
    }
  }
}
</style>
